import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Project } from "@/common/define";
import type {
  FilterProjectsRequest,
  ProjectResponse,
} from "@/features/projects/services/projectsApi";

interface ProjectState {
  folderRootId: string;
  loading: boolean;
  error: string | null;
  projectId: number | null;
  projects: Project[];
  projectsByCompanyId: Project[];
  projectsLoading: boolean;
  projectsError: string | null;
  lastProjectsFetch: number | null;
  cacheExpiry: number;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  sortOptions: {
    sortBy: string;
    sortOrder: "asc" | "desc";
  };
}

const CACHE_DURATION = 20 * 60 * 1000; // 20 phút

// Khôi phục projects từ localStorage nếu có
const getPersistedProjects = (): Project[] => {
  try {
    const stored = localStorage.getItem("cachedProjects");
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const getPersistedLastFetch = (): number | null => {
  try {
    const stored = localStorage.getItem("lastProjectsFetch");
    return stored ? parseInt(stored) : null;
  } catch {
    return null;
  }
};

const getInitialSortOptions = () => {
  const userSortOption = localStorage.getItem("userSortOption") || "date";
  switch (userSortOption) {
    case "name-asc":
      return { sortBy: "title", sortOrder: "asc" as const };
    case "name-desc":
      return { sortBy: "title", sortOrder: "desc" as const };
    case "date":
    default:
      return { sortBy: "updated", sortOrder: "desc" as const };
  }
};

const initialState: ProjectState = {
  folderRootId: localStorage.getItem("folderRootId") || "",
  projectId: null,
  loading: false,
  error: null,
  projects: getPersistedProjects(),
  projectsByCompanyId: [],
  projectsLoading: false,
  projectsError: null,
  lastProjectsFetch: getPersistedLastFetch(),
  cacheExpiry: CACHE_DURATION,
  pagination: {
    page: 1,
    pageSize: 30,
    total: 0,
  },
  sortOptions: getInitialSortOptions(),
};

const projectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {
    getFolderRootId: (state, _action: PayloadAction<{ projectId: number }>) => {
      state.loading = true;
      state.error = null;
    },
    setFolderRootId: (state, action: PayloadAction<string>) => {
      localStorage.setItem("folderRootId", action.payload);
      localStorage.setItem("folderId", action.payload);
      state.folderRootId = action.payload;
      //console.log("Folder root ID set to:", action.payload);
      state.loading = false;
    },
    getFolderRootIdFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
      state.folderRootId = "";
    },
    getFolderRootIdWithCreateIfNotExists: (
      state,
      _action: PayloadAction<{ projectId: number; companyId: string }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    createFolderRootProject: (
      state,
      _action: PayloadAction<{ projectId: number }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    createFolderRootProjectSuccess: (state, action: PayloadAction<string>) => {
      localStorage.setItem("folderRootId", action.payload);
      localStorage.setItem("folderId", action.payload);
      state.folderRootId = action.payload;
      state.loading = false;
    },
    createFolderRootProjectFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    getProjects: (state, action: PayloadAction<any>) => {
      const { forceRefresh = false } = action.payload || {};

      // Kiểm tra xem có nên skip không
      const now = Date.now();
      const cacheValid =
        state.lastProjectsFetch &&
        now - state.lastProjectsFetch < state.cacheExpiry;

      // Nếu đang loading và không force refresh thì không làm gì
      if (state.projectsLoading && !forceRefresh) {
        return;
      }

      // Nếu có cache valid và không force refresh thì không làm gì
      if (cacheValid && !forceRefresh && state.projects.length > 0) {
        return;
      }

      // Bắt đầu loading
      state.projectsLoading = true;
      state.projectsError = null;
    },

    setProjects: (state, action: PayloadAction<Project[]>) => {
      state.projects = action.payload;
      state.projectsLoading = false;
      state.projectsError = null;
      state.lastProjectsFetch = Date.now();

      // Lưu vào localStorage để persist
      try {
        localStorage.setItem("cachedProjects", JSON.stringify(action.payload));
        localStorage.setItem(
          "lastProjectsFetch",
          state.lastProjectsFetch.toString()
        );
      } catch (e) {
        console.warn("Failed to persist projects to localStorage:", e);
      }
    },
    setProjectsPagination: (
      state,
      action: PayloadAction<{ page: number; pageSize: number; total: number }>
    ) => {
      state.pagination = action.payload;
    },

    getProjectsFailure: (state, action: PayloadAction<string>) => {
      state.projectsError = action.payload;
      state.projectsLoading = false;
    },

    resetProjectsLoading: (state) => {
      state.projectsLoading = false;
    },
    clearProjectsCache: (state) => {
      state.projects = [];
      state.lastProjectsFetch = null;
      state.projectsError = null;
      // Reset pagination to initial state
      state.pagination = {
        page: 1,
        pageSize: 30,
        total: 0,
      };

      // Xóa cache trong localStorage
      try {
        localStorage.removeItem("cachedProjects");
        localStorage.removeItem("lastProjectsFetch");
      } catch (e) {
        console.warn("Failed to clear projects cache from localStorage:", e);
      }
    },
    setChosenProjectId: (state, action: PayloadAction<number | null>) => {
      state.projectId = action.payload;
    },

    // Filter projects actions
    filterProjects: (state, action: PayloadAction<FilterProjectsRequest>) => {
      state.projectsLoading = true;
      state.projectsError = null;
    },

    setFilteredProjects: (
      state,
      action: PayloadAction<{
        projects: Project[];
        totalCount: number;
        page: number;
        pageSize: number;
      }>
    ) => {
      state.projects = action.payload.projects;
      state.pagination = {
        page: action.payload.page,
        pageSize: action.payload.pageSize,
        total: action.payload.totalCount,
      };
      state.projectsLoading = false;
      state.projectsError = null;
    },

    filterProjectsFailure: (state, action: PayloadAction<string>) => {
      state.projectsError = action.payload;
      state.projectsLoading = false;
    },

    // Sort actions
    setSortOptions: (
      state,
      action: PayloadAction<{ sortBy: string; sortOrder: "asc" | "desc" }>
    ) => {
      state.sortOptions = action.payload;
      // Store in localStorage for persistence
      let storageValue: string;
      if (action.payload.sortBy === "title") {
        storageValue =
          action.payload.sortOrder === "asc" ? "name-asc" : "name-desc";
      } else {
        storageValue = "date";
      }
      localStorage.setItem("userSortOption", storageValue);
    },
    getProjectsByCompanyIdRequest: (state, _action: PayloadAction<number>) => {
      state.projectsLoading = true;
      state.projectsError = null;
    },
    getProjectsByCompanyIdSuccess: (
      state,
      action: PayloadAction<Project[]>
    ) => {
      state.projectsLoading = false;
      state.projectsByCompanyId = action.payload;
    },

    getProjectsByCompanyIdFailure: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.projectsLoading = false;
      state.projectsByCompanyId = [];
      state.projectsError = action.payload ?? "Error fetching projects";
    },
  },
});

export const projectActions = projectSlice.actions;
export const projectReducer = projectSlice.reducer;
